import { getStarterBuildingGrants } from "@/event/session/eventStarterBuildingRemoval";
import { FrameName } from "@/game/entities/config";
import useMapActions from "@/hooks/useMapActions";
import useTileRepository, {
	isTilerepositoryInitialized,
} from "@/hooks/useTileRepository";
import type { EventStarterBuildingGrant } from "@/types";

const VALID_BUILDINGS = new Set<string>(
	Object.values(FrameName) as Array<string>,
);
const pendingCleanupByKey = new Map<string, ReadonlySet<FrameName>>();
const completedCleanupKeys = new Set<string>();

function cleanupDedupeKey(parts: Array<string>): string {
	return parts.join("|");
}

function clearBuildingsOnMap(buildings: ReadonlySet<FrameName>): void {
	if (!isTilerepositoryInitialized()) return;
	useMapActions().init();
	useTileRepository().removePlacedBuildings(buildings);
}

function clearStarterBuildings(
	params: {
		eventId: string;
		startBuildingClaims?: ReadonlyArray<EventStarterBuildingGrant> | null;
		overviewGrants?: ReadonlyArray<EventStarterBuildingGrant> | null;
	},
	onceKeys: Set<string>,
): boolean {
	const grants = getStarterBuildingGrants({
		startBuildingClaims: params.startBuildingClaims,
		overviewGrants: params.overviewGrants,
	});
	if (!grants.length) return false;

	const dedupe = cleanupDedupeKey([
		"start",
		params.eventId,
		...grants.map(claim => claim.frameName.trim()).sort(),
	]);
	if (onceKeys.has(dedupe) || completedCleanupKeys.has(dedupe)) return false;

	const mapFrameNames = new Set<FrameName>();
	for (const grant of grants) {
		const raw = grant.frameName?.trim();
		if (!raw || !VALID_BUILDINGS.has(raw)) continue;
		mapFrameNames.add(raw as FrameName);
	}

	if (!mapFrameNames.size) return false;

	if (!isTilerepositoryInitialized()) {
		pendingCleanupByKey.set(dedupe, mapFrameNames);
		return false;
	}

	pendingCleanupByKey.delete(dedupe);
	clearBuildingsOnMap(mapFrameNames);
	onceKeys.add(dedupe);
	completedCleanupKeys.add(dedupe);
	return true;
}

/**
 * Removes starter event buildings from the map when {@link EventOverviewResult.starterMapCleaned}
 * is false (see `applyStarterMapCleanup` in overviewPatch). Cold loads may receive the overview
 * before the tile repository exists, so cleanup is queued and flushed after map init.
 */
export function clearStarterBuildingsOnEventStart(
	params: {
		eventId: string;
		startBuildingClaims?: ReadonlyArray<EventStarterBuildingGrant> | null;
		overviewGrants?: ReadonlyArray<EventStarterBuildingGrant> | null;
	},
	onceKeys: Set<string>,
): boolean {
	return clearStarterBuildings(params, onceKeys);
}

/** Flushes any queued same-type event-start starter-building removals. */
export function flushPendingStarterBuildingsOnEventStart(): boolean {
	if (!isTilerepositoryInitialized()) return false;
	if (!pendingCleanupByKey.size) return false;

	useMapActions().init();

	let cleared = false;
	for (const [dedupe, buildings] of pendingCleanupByKey) {
		pendingCleanupByKey.delete(dedupe);
		if (completedCleanupKeys.has(dedupe)) continue;

		clearBuildingsOnMap(buildings);
		completedCleanupKeys.add(dedupe);
		cleared = true;
	}

	return cleared;
}
